import { FontAwesomeIcon } from '@fortawesome/react-native-fontawesome'
import { useBottomTabBarHeight } from '@react-navigation/bottom-tabs'
import { getDefaultHeaderHeight } from '@react-navigation/elements'
import { useFocusEffect } from '@react-navigation/native'
import { useGlobalSearchParams, useNavigation, useRouter } from 'expo-router'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Dimensions,
  FlatList,
  Platform,
  StatusBar,
  StyleSheet,
  TouchableOpacity,
  useWindowDimensions,
  View,
} from 'react-native'
import { Dialog, Paragraph } from 'react-native-paper'
import { useSafeAreaFrame, useSafeAreaInsets } from 'react-native-safe-area-context'
import { useDispatch, useSelector } from 'react-redux'
import { AlertConfirmation } from '~components/AlertConfirmation'
import ChatSection from '~components/ChatSection'
import { Item } from '~components/header/HeaderItems'
import ShareChatPopup, { ShareChatPopupProps } from '~components/ShareChatPopup.web'
import { EVENT_CHAT_SHARED } from '~constants/Analytics'

import { ChatProvider } from '~context/ChatProvider'
import useClass from '~hooks/useClass'
import {
  analytics,
  doc,
  firestore,
  functions,
  httpsCallable,
  logEvent,
  onSnapshot,
} from '~providers/firebase'
import Share from '~providers/share'
import { ApplicationState } from '~redux'
import { removeChatImage, setChat } from '~redux/chat/actions'
import { addRecentChat, removeRecentChat } from '~redux/recent'
import ChatService from '~services/chat'
import { useTheme } from '~theme/ThemeManager'
import { IChatLayout } from '~types'

function useBottomTabBarHeightMaybe() {
  try {
    return useBottomTabBarHeight()
  } catch (error) {
    console.log('useBottomTabBarHeightMaybe', error)
    return 0
  }
}

export default function ChatScreen(): React.JSX.Element {
  const { folderId, chatId, userId } = useGlobalSearchParams<{
    folderId: string
    chatId: string
    userId?: string
    source?: string
    sourceTarget?: string
  }>()

  const path = userId ? `users/${userId}/chats/${chatId}` : `classes/${folderId}/chats/${chatId}`

  const router = useRouter()
  const navigation = useNavigation()

  const dimensionsHook = React.useMemo(
    () => (Platform.OS === 'web' ? useWindowDimensions : useSafeAreaFrame),
    [],
  )
  const windowDimensions = dimensionsHook()
  const insets = useSafeAreaInsets()
  const headerHeight = React.useMemo(
    () => getDefaultHeaderHeight(windowDimensions, false, insets.top),
    [windowDimensions, insets],
  )

  const { chat, selectedObject } = useSelector((state: ApplicationState) => state.chat)
  const dispatch = useDispatch()
  const { theme } = useTheme()
  const list = useRef<FlatList>(null)
  const [isShareLoading, setIsShareLoading] = useState(false)
  const [shareChatPopupVisible, setShareChatPopupVisible] = useState(false)
  const [shareUrl, setShareUrl] = useState('')
  const { user, profile } = useSelector((state: ApplicationState) => state.auth)
  const currentUserId = user!.uid

  // TODO: redirect on classError
  const [classData, classError] = useClass(folderId)
  const isAuthor = chat.isAuthor
  const isTeacher = currentUserId && (classData?.teachers ?? []).includes(currentUserId)
  const isReadOnly = !isTeacher && !isAuthor // TODO: template?

  const classId = folderId
  const tabHeight = useBottomTabBarHeightMaybe()
  const [showDeleteImageConfirmation, setShowDeleteImageConfirmation] = useState(false)

  React.useLayoutEffect(() => {
    const deleteImage = () => {
      const src = chat?.sections[selectedObject].image.src
      if (selectedObject === -1 || src === '') {
        return
      }

      const url = new URL(src)

      dispatch(
        removeChatImage({
          chat: { id: chat.id, path: chat.path },
          index: selectedObject,
          path: chat.path,
          filename: (url.pathname.split('/').pop() ?? '').split('%2F').pop() ?? '',
        }),
      )
    }

    const onDeleteImagePressed = () => {
      if (selectedObject > -1) {
        setShowDeleteImageConfirmation(true)
      }
    }

    const share = async () => {
      if (isShareLoading) {
        return
      }

      setIsShareLoading(true)

      try {
        let url = shareUrl

        if (!url) {
          const response = await httpsCallable<{ path: string }, { url: string }>(
            functions,
            'shareChat',
          )({
            path: chat.path,
          })
          url = response.data.url
          setShareUrl(url)
          await logEvent(analytics, EVENT_CHAT_SHARED)
        }

        if (url) {
          if (Platform.OS === 'web') {
            setShareChatPopupVisible(true)
          } else {
            const title = `Shared Chat: ${chat.name}`

            await Share.open({
              title,
              subject: title,
              url,
            })
          }
        }
      } catch (error) {
        console.log(error)
      } finally {
        setIsShareLoading(false)
      }
    }

    navigation.getParent()?.setOptions({
      headerTitle: Dimensions.get('window').width < 480 || chat.id !== chatId ? '' : chat.name,
      headerRight: () =>
        isTeacher || chat.isAuthor ? (
          <View style={styles.navIconsContainer}>
            <TouchableOpacity
              onPress={onDeleteImagePressed}
              style={styles.navIconsButton}
              disabled={selectedObject === -1 || chat?.sections[selectedObject].image.src === ''}
            >
              <FontAwesomeIcon
                color={
                  selectedObject === -1 || chat?.sections[selectedObject].image.src === ''
                    ? theme[300]
                    : theme.primary
                }
                size={24}
                icon={['far', 'trash-alt']}
              />
            </TouchableOpacity>
            <TouchableOpacity
              onPress={() => {
                router.navigate(`./${chatId}/edit`)
              }}
              style={styles.navIconsButton}
            >
              <FontAwesomeIcon color={theme.primary} size={24} icon={['far', 'edit']} />
            </TouchableOpacity>
            <TouchableOpacity
              onPress={share}
              style={styles.navIconsButton}
              disabled={isShareLoading}
            >
              <FontAwesomeIcon
                color={isShareLoading ? theme[300] : theme.primary}
                size={24}
                icon={['far', 'share-alt']}
              />
            </TouchableOpacity>
            {showDeleteImageConfirmation && (
              <AlertConfirmation
                visible={showDeleteImageConfirmation}
                setVisible={setShowDeleteImageConfirmation}
                confirmAction={deleteImage}
              >
                <Dialog.Title>Delete Image?</Dialog.Title>
                <Dialog.Content>
                  <Paragraph>Deleting this image will remove it from your chat.</Paragraph>
                </Dialog.Content>
              </AlertConfirmation>
            )}
          </View>
        ) : (
          <>
            <Item
              title="Duplicate"
              icon={['far', 'copy']}
              onPress={() => {
                if (currentUserId && classId) {
                  ChatService.duplicateChat(currentUserId, profile.displayName, classId, chat)
                }
              }}
            />
          </>
        ),
    })
  }, [
    dispatch,
    navigation,
    selectedObject,
    theme,
    chat,
    isShareLoading,
    shareUrl,
    isTeacher,
    currentUserId,
    profile,
    classId,
    showDeleteImageConfirmation,
    chatId,
    router,
  ])

  useFocusEffect(
    useCallback(() => {
      let didCancel = false

      let subscriberParent: (() => void) | undefined = undefined

      // Parse the path
      const replyPath =
        classId !== undefined && chatId !== undefined && userId === undefined
          ? `users/${currentUserId}/chats/${chatId}`
          : undefined

      const onError = (error) => {
        dispatch(removeRecentChat(folderId, chatId, userId))
        // navigation.goBack()
        console.log(error)
      }

      const subscriptionBuilder = (
        path: string,
        callbackNotFound: (() => void) | undefined = undefined,
        callBackSuccess: (() => void) | undefined = undefined,
      ) =>
        onSnapshot(
          doc(firestore, path),
          (snapshot) => {
            if (typeof snapshot.exists === 'function' ? !snapshot.exists() : !snapshot.exists) {
              dispatch(removeRecentChat(folderId, chatId, userId))

              if (callbackNotFound) {
                callbackNotFound()
              } else {
                // If parent doesn't exist go back
                navigation.goBack()
              }
            } else {
              if (callBackSuccess) {
                callBackSuccess()
              }

              const chat = ChatService.chatFromSnapshot(snapshot)
              if (!didCancel) {
                dispatch(
                  addRecentChat(
                    chat,
                    folderId,
                    chatId,
                    currentUserId !== userId ? userId : undefined,
                  ),
                )
                dispatch(setChat(chat))
              }
            }
          },
          onError,
        )

      const subscriber = subscriptionBuilder(
        replyPath ?? path,
        replyPath !== undefined
          ? () => {
              subscriberParent = subscriptionBuilder(path)
            }
          : undefined,
        () => {
          if (subscriberParent !== undefined) {
            subscriberParent()
          }
        },
      )

      return () => {
        didCancel = true
        subscriber()
        if (subscriberParent !== undefined) {
          subscriberParent()
        }
      }
    }, [chatId, classId, dispatch, folderId, navigation, path, userId, currentUserId]),
  )

  const data = useMemo(() => {
    if (chat.id !== chatId) {
      return []
    }

    return Object.entries(chat?.sections ?? {})
      .sort(([, a], [, b]) => (a.order ?? 0) - (b.order ?? 0))
      .map(([key]) => parseInt(key))
  }, [chat.id, chat?.sections, chatId])

  const small = Dimensions.get('window').width < 480 || Dimensions.get('window').height < 480

  const displayLayout: IChatLayout = small
    ? {
        rows: 1,
        columns: 1,
      }
    : chat.layout

  const chatSectionOptions = useMemo(() => {
    const availableHeight =
      windowDimensions.height - (StatusBar.currentHeight || 0) - tabHeight - headerHeight

    const heightEach = Math.floor(availableHeight / displayLayout.rows)
    const heightMin = 160

    const height = Math.max(heightEach, heightMin)

    return {
      scrollEnabled: heightEach < heightMin,
      style: {
        flexBasis: 0,
        minHeight: height,
        height,
      },
    }
  }, [windowDimensions, headerHeight, displayLayout.rows, tabHeight])

  const shareChatPopupProps: ShareChatPopupProps = {
    visible: shareChatPopupVisible,
    setVisible: (visible: boolean) => setShareChatPopupVisible(visible),
    url: shareUrl,
  }

  useEffect(() => {
    if (Platform.OS !== 'web' && (small || chatSectionOptions.scrollEnabled)) {
      try {
        const ref = list.current
        if (!ref) return
        ref.setNativeProps({ scrollEnabled: selectedObject === -1 })
      } catch (error) {
        console.log('Failed setNativeProps scrollEnabled', error)
      }
    }
  }, [chatSectionOptions.scrollEnabled, list, selectedObject, small])

  const renderItem = ({ index, item }) => (
    <ChatSection
      style={chatSectionOptions.style}
      index={item}
      isTeacher={isTeacher}
      isAuthor={isAuthor}
      isReadOnly={isReadOnly}
    />
  )

  return (
    <ChatProvider>
      <View style={styles.container}>
        {Platform.OS === 'web' && <ShareChatPopup {...shareChatPopupProps} />}
        <FlatList
          ref={list}
          key={`chat_${displayLayout.rows}_${displayLayout.columns}`}
          style={styles.chat}
          scrollEnabled={small || chatSectionOptions.scrollEnabled}
          numColumns={displayLayout.columns}
          snapToAlignment="start"
          snapToInterval={chatSectionOptions.style.height}
          decelerationRate="fast"
          pagingEnabled
          data={data}
          renderItem={renderItem}
          keyExtractor={(item) => item.toString()}
          getItemLayout={(data, index) => ({
            length: chatSectionOptions.style.height,
            offset: chatSectionOptions.style.height * index,
            index,
          })}
        />
      </View>
    </ChatProvider>
  )
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  chat: {
    flex: 1,
  },
  navIconsContainer: {
    flexDirection: 'row',
    paddingEnd: 10,
  },
  navIconsButton: {
    marginLeft: 15,
    padding: 10,
  },
})
